import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ShieldCheck } from "lucide-react";
import { usePreQualifier } from "@/context/PreQualifierContext";
import { LogoMark } from "@/components/Logo";

export default function Navbar({ variant = "light" }) {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const preq = usePreQualifier();
  const isDark = variant === "dark";
  const onApplyPage = location.pathname === "/apply";
  const handleApply = () => {
    // Intercept with pre-qualifier modal unless we're already on /apply
    if (onApplyPage) return nav("/apply");
    preq?.open();
  };

  React.useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    fn();
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const links = [
    { href: "/", label: "Home" },
    { href: "/#how", label: "How it works" },
    { href: "/#pricing", label: "Pricing" },
    { href: "/#faq", label: "FAQ" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isDark ? "bg-navy-deep/95 border-b border-white/10" :
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-line shadow-sm" :
        "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 h-[76px] flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
          <LogoMark size={40} />
          <div className={`font-display font-extrabold text-[20px] tracking-[-0.035em] ${isDark ? "text-white" : "text-navy"}`}>
            Swift<span className="text-emerald">Cash</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-[14.5px] font-semibold transition-colors ${isDark ? "text-white/80 hover:text-white" : "text-navy/75 hover:text-navy"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 shrink-0">
          <Link
            to="/admin/login"
            data-testid="nav-admin-login"
            className={`h-11 px-5 rounded-lg border font-semibold text-[13.5px] transition-colors inline-flex items-center gap-1.5 ${
              isDark ? "border-white/20 text-white hover:bg-white/5" : "border-navy/15 text-navy hover:bg-navy/5"
            }`}
          >
            <ShieldCheck className="h-4 w-4" strokeWidth={2.2} />
            Admin
          </Link>
          <button
            onClick={handleApply}
            data-testid="nav-apply-btn"
            className="btn-shine group inline-flex items-center gap-1.5 h-11 px-5 rounded-lg bg-emerald hover:bg-emerald-2 text-white font-bold text-[14px] transition-colors shadow-emerald"
          >
            Apply Now
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
          </button>
        </div>

        <button
          className={`lg:hidden p-2 -mr-2 ${isDark ? "text-white" : "text-navy"}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className={`lg:hidden border-t ${isDark ? "border-white/10 bg-navy-deep" : "border-line bg-white"}`}>
          <div className="px-5 py-5 flex flex-col gap-1">
            {links.map((l) => (
              <Link key={l.href} to={l.href} className={`py-3 text-[15px] font-semibold ${isDark ? "text-white" : "text-navy"}`} onClick={() => setOpen(false)}>
                {l.label}
              </Link>
            ))}
            <Link to="/admin/login" className={`py-3 text-[14px] font-medium ${isDark ? "text-white/60" : "text-navy/60"}`} onClick={() => setOpen(false)}>
              Admin
            </Link>
            <button
              onClick={() => { setOpen(false); handleApply(); }}
              data-testid="nav-mobile-apply-btn"
              className="mt-3 h-12 rounded-lg bg-emerald text-white font-bold shadow-emerald flex items-center justify-center gap-2"
            >
              Apply Now <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
